
import React, { useState } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { BankAccountsStatus, Invoice, Filters, InvoiceStatus } from "@/types/payments";
import BulkActions from "@/components/payments/BulkActions";
import InvoicesTable from "@/components/payments/InvoicesTable";
import SearchAndFilterBar from "./table/SearchAndFilterBar";
import ResultsSummary from "./table/ResultsSummary";

interface InvoicesSectionProps {
  activeTab: string;
  selectedInvoices: string[];
  filteredInvoices: Invoice[];
  sortedInvoices: Invoice[];
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  filters: Filters;
  activeFilters: Filters;
  handleFilterChange: (key: keyof Filters, value: any) => void;
  handleStatusFilterChange?: (status: InvoiceStatus | 'all') => void;
  resetFilters: () => void;
  applyFilters: () => void;
  uniqueCurrencies: string[];
  toggleSelectInvoice: (invoiceId: string) => void;
  toggleSelectAll: () => void;
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  handleBulkPayment: () => void;
  handleBulkCancel: () => void;
  handleBulkExport: () => void;
  updatePaymentDate: (invoiceId: string, date: Date) => void;
  onRowClick: (invoice: Invoice) => void;
  handleSinglePay?: (invoiceId: string, paymentDate: Date) => void;
  handleSingleCancel?: (invoiceId: string) => void;
  handleSingleExport?: (invoiceId: string) => void;
  handleDownloadInvoice?: (invoiceId: string, fileName: string) => void;
  onEditPayment?: (invoiceId: string) => void;
  onEditVendor?: (invoiceId: string) => void;
  onEditInvoice?: (invoiceId: string) => void;
  handleConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
  allInvoices?: Invoice[];
  getStatusBadge: (status: string) => any;
  formatCurrency: (amount: number, currency: string) => string;
  /** Pass-through — see BankAccountsStatus's docstring (#498 whole-branch review finding I2). */
  hasBankAccounts?: BankAccountsStatus;
}

const InvoicesSection: React.FC<InvoicesSectionProps> = ({
  activeTab,
  selectedInvoices,
  filteredInvoices,
  sortedInvoices,
  showFilters,
  setShowFilters,
  filters,
  activeFilters,
  handleFilterChange,
  handleStatusFilterChange,
  resetFilters,
  applyFilters,
  uniqueCurrencies,
  toggleSelectInvoice,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  handleBulkPayment,
  handleBulkCancel,
  handleBulkExport,
  updatePaymentDate,
  onRowClick,
  handleSinglePay,
  handleSingleCancel,
  handleSingleExport,
  handleDownloadInvoice,
  onEditPayment,
  onEditVendor,
  onEditInvoice,
  handleConfirmMatch,
  allInvoices = [],
  getStatusBadge,
  formatCurrency,
  hasBankAccounts
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Update vendor filter with search query
    handleFilterChange("vendor", value);
  };
  
  return (
    <TabsContent value={activeTab} className="mt-0">
      <div className="flex flex-col space-y-4 mb-4">
        {/* Search and Filter Controls */}
        <SearchAndFilterBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filters={filters}
          handleFilterChange={handleFilterChange}
          handleStatusFilterChange={handleStatusFilterChange}
          resetFilters={resetFilters}
          applyFilters={applyFilters}
          uniqueCurrencies={uniqueCurrencies}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          activeFilters={activeFilters}
        />
        
        {/* Bulk Actions */}
        <BulkActions 
          selectedInvoices={selectedInvoices}
          handleBulkPayment={handleBulkPayment}
          handleBulkCancel={handleBulkCancel}
          handleBulkExport={handleBulkExport}
          activeTab={activeTab}
          allInvoices={allInvoices}
        />
      </div>
      
      {/* Invoices Table */}
      <InvoicesTable 
        sortedInvoices={sortedInvoices}
        selectedInvoices={selectedInvoices}
        toggleSelectInvoice={toggleSelectInvoice}
        toggleSelectAll={toggleSelectAll}
        handleSort={handleSort}
        renderSortIcon={renderSortIcon}
        getStatusBadge={getStatusBadge}
        formatCurrency={formatCurrency}
        filteredInvoices={filteredInvoices}
        activeTab={activeTab}
        updatePaymentDate={updatePaymentDate}
        onRowClick={onRowClick}
        onPay={handleSinglePay}
        onCancel={handleSingleCancel}
        onExport={handleSingleExport}
        onDownload={handleDownloadInvoice}
        onEditPayment={onEditPayment}
        onEditVendor={onEditVendor}
        onEditInvoice={onEditInvoice}
        onConfirmMatch={handleConfirmMatch}
        hasBankAccounts={hasBankAccounts}
      />
      
      {/* Results summary */}
      <ResultsSummary 
        sortedInvoices={sortedInvoices}
        filteredInvoices={filteredInvoices}
        filters={filters}
      />
    </TabsContent>
  );
};

export default InvoicesSection;
